import type { MantineColorScheme } from '@mantine/core';
import { Capacitor, registerPlugin, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { BROWSER_BAR_HEX } from '@/app/theme-tokens';

type AppearanceMode = 'light' | 'dark' | 'system';

type AppearancePlugin = {
	set: (options: { mode: AppearanceMode }) => Promise<void>;
};

/** Implemented in the app targets: ios/App/App/Appearance.swift and android/.../AppearancePlugin.java. */
const Appearance = registerPlugin<AppearancePlugin>('Appearance');

const ignore = () => {};

/** Fire and forget: a missing plugin or an older native build must not break theming. */
function attempt(call: () => Promise<void>): void {
	Promise.resolve().then(call).catch(ignore);
}

/**
 * Brings everything outside the web view in line with the app's scheme: the browser bar on the web,
 * and on native the status bar, sheets, keyboard and the next launch screen.
 */
export function syncAppearance(colorScheme: MantineColorScheme, resolved: 'light' | 'dark'): void {
	document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BROWSER_BAR_HEX[resolved]);

	if (!Capacitor.isNativePlatform()) {
		return;
	}

	if (Capacitor.isPluginAvailable('Appearance')) {
		attempt(() => Appearance.set({ mode: colorScheme === 'auto' ? 'system' : colorScheme }));
	}
	// Set explicitly: SystemBarsStyle.Default follows the OS, which can disagree with the app.
	attempt(() => SystemBars.setStyle({ style: resolved === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light }));
}
